import React from "react";
import styles from "./SetupSteps.module.scss";

export type Step = { key: string; label: string };

type Props = {
  steps: Step[];
  current: number; // 1-based index
  rightLabel?: string;
  onStepClick?: (step: Step, index: number) => void;
};

export default function SetupSteps({
  steps,
  current,
  rightLabel,
  onStepClick,
}: Props) {
  const pct = Math.max(
    0,
    Math.min(100, Math.round(((current - 1) / steps.length) * 100))
  );

  return (
    <div className={styles.wrapper} role="region" aria-label="Setup progress">
      <div className={styles.headerRow}>
        <span className={styles.stepText}>
          Step {current} of {steps.length}
        </span>
        <span className={styles.percent} aria-live="polite">
          {rightLabel ?? `${pct}% Complete`}
        </span>
      </div>

      <ol className={styles.track} role="list">
        {steps.map((s, idx) => {
          const i = idx + 1;
          const state =
            i < current ? "done" : i === current ? "active" : "todo";

          const clickable = !!onStepClick;

          return (
            <li
              key={s.key}
              className={`${styles.segment} ${styles[state]} ${
                clickable ? styles.clickable : ""
              }`}
              aria-current={i === current ? "step" : undefined}
              title={s.label}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : -1}
              onClick={() => clickable && onStepClick?.(s, i)}
              onKeyDown={(e) => {
                if (clickable && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onStepClick?.(s, i);
                }
              }}
            >
              <span className={styles.segmentLabel}>{s.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
